import { formatBrl, getModule, moduleSetupCents } from '@estac1/shared';
import { Button } from '@estac1/ui';
import type { UseMutationResult } from '@tanstack/react-query';
import { Camera, DoorOpen, Printer } from 'lucide-react';
import type { Lot } from './types.js';

export type ToggleableModuleKey = 'damage_photos' | 'barrier' | 'printer';

export interface PendingModuleToggle {
  lot: Lot;
  key: ToggleableModuleKey;
  enabled: boolean;
}

const flatPrice = (key: ToggleableModuleKey): number => {
  const def = getModule(key);
  return def?.billing.kind === 'flat' ? def.billing.priceCents : 0;
};

const setupPrice = (key: ToggleableModuleKey): number => {
  const def = getModule(key);
  return def ? moduleSetupCents(def) : 0;
};

const META: Record<
  ToggleableModuleKey,
  {
    label: string;
    priceCents: number;
    setupCents: number;
    Icon: typeof Camera;
    enabledFlag: keyof Lot;
  }
> = {
  damage_photos: {
    label: 'fotos de avaria',
    priceCents: flatPrice('damage_photos'),
    setupCents: 0,
    Icon: Camera,
    enabledFlag: 'damagePhotosEnabled',
  },
  barrier: {
    label: 'cancela',
    priceCents: flatPrice('barrier'),
    setupCents: setupPrice('barrier'),
    Icon: DoorOpen,
    enabledFlag: 'barrierEnabled',
  },
  printer: {
    label: 'impressora',
    priceCents: flatPrice('printer'),
    setupCents: setupPrice('printer'),
    Icon: Printer,
    enabledFlag: 'printerEnabled',
  },
};

export const moduleToggleLabel = (key: ToggleableModuleKey) => META[key].label;
export const moduleTogglePriceLabel = (key: ToggleableModuleKey) => formatBrl(META[key].priceCents);

/** Confirm-dialog copy: monthly MRR + one-time setup when enabling hardware. */
export const moduleToggleConfirmDescription = (pending: PendingModuleToggle): string => {
  const meta = META[pending.key];
  const monthly = formatBrl(meta.priceCents);
  if (!pending.enabled) {
    return `Remove o módulo "${meta.label}" (${monthly}/mês) em ${pending.lot.name}.`;
  }
  if (meta.setupCents > 0) {
    return `Adiciona o módulo "${meta.label}" (${monthly}/mês) em ${pending.lot.name}. Cobrar à parte a instalação de ${formatBrl(meta.setupCents)} (fora do Asaas).`;
  }
  if (pending.key === 'damage_photos') {
    return `Adiciona o módulo "${meta.label}" (${monthly}/mês) em ${pending.lot.name}. Na primeira ativação entra em teste grátis de 30 dias.`;
  }
  return `Adiciona o módulo "${meta.label}" (${monthly}/mês) em ${pending.lot.name}.`;
};

interface LotModuleToggleButtonsProps {
  lot: Lot;
  pending: boolean;
  onToggle: (lot: Lot, key: ToggleableModuleKey, enabled: boolean) => void;
}

export const LotModuleToggleButtons = ({ lot, pending, onToggle }: LotModuleToggleButtonsProps) => (
  <>
    {(Object.keys(META) as ToggleableModuleKey[]).map((key) => {
      const { label, Icon, enabledFlag } = META[key];
      const enabled = Boolean(lot[enabledFlag]);
      return (
        <Button
          key={key}
          size="sm"
          variant={enabled ? 'ghost' : 'outline'}
          disabled={pending}
          onClick={() => onToggle(lot, key, !enabled)}
        >
          <Icon className="h-4 w-4" /> {enabled ? `Desativar ${label}` : `Ativar ${label}`}
        </Button>
      );
    })}
  </>
);

export type ModuleToggleMutation = UseMutationResult<
  unknown,
  unknown,
  { lotId: string; key: ToggleableModuleKey; enabled: boolean },
  unknown
>;
